import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
//   Fade,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Shared Components
import HeroSlide from './shared/HeroSlide';

const HeroSlider = ({ section, isLoading }) => {
  const navigate = useNavigate();
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);

  const items = useMemo(() => section?.items || [], [section?.items]);
  const config = section?.config || {};
  const interval = config.interval || 5000;

  const handleSlideClick = useCallback((slide) => {
    if (slide.link_url) {
      navigate(slide.link_url);
    }
  }, [navigate]);

  const handleNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, items.length]);

  const handlePrevious = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating, items.length]);

  const handleDotClick = useCallback((index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setAutoplay(false); // Stop autoplay when user manually changes slide
    setTimeout(() => {
      setCurrentIndex(index);
      setIsAnimating(false);
    }, 300);
  }, [isAnimating]);

  // Handle autoplay control from user interaction
  const handleAutoplayChange = useCallback((shouldAutoplay) => {
    setAutoplay(shouldAutoplay);
  }, []);

  // Intersection Observer for fade-in animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.querySelector('[data-hero-animate]');
    if (element) {
      observer.observe(element);
    }

    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Autoplay
  useEffect(() => {
    if (!autoplay || items.length <= 1) return;

    const timer = setInterval(() => {
      handleNext();
    }, interval);

    return () => clearInterval(timer);
  }, [autoplay, interval, items.length, handleNext]);

  // Pause autoplay on hover
  const shouldAutoplay = autoplay && !isAnimating;

  // Reset animation state when slide changes
  useEffect(() => {
    setIsVisible(false);
    setTimeout(() => setIsVisible(true), 100);
  }, [currentIndex]);

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          position: 'relative', 
          height: { xs: 300, md: 500 }, 
          overflow: 'hidden',
          backgroundColor: 'grey.100'
        }}
      />
    );
  }

  if (!items.length) return null;

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        height: { xs: 300, md: 500 }, 
        overflow: 'hidden',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      data-hero-animate
    >
      <HeroSlide
        slide={items[currentIndex]}
        currentIndex={currentIndex}
        totalSlides={items.length}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onDotClick={handleDotClick}
        showNavigation={items.length > 1}
        showDots={items.length > 1}
        autoplay={shouldAutoplay}
        interval={config.interval || 5000}
        onClick={handleSlideClick}
        onAutoplayChange={handleAutoplayChange}
      />
    </Box>
  );
};

export default HeroSlider;
